import { create } from 'zustand';

import type { ViewMode } from '@/shared/ui/ViewToggle';
import type { DeliveryMethod, PaymentMethod } from '@/shared/services/api/types';
import { deviceStorage } from '@/shared/services/storage/device-storage';

/**
 * Préférences d'affichage.
 *
 * Ce qui vit ici ne concerne que la forme : ce que le compte a le droit de faire
 * est dans `session.store`, et ne se règle pas depuis l'application. Une
 * préférence se choisit une fois et se retrouve au lancement suivant : la
 * redemander a chaque ouverture serait la traiter comme un accident.
 *
 * Deux réglages, uniques pour tout le compte et non par écran : un vendeur qui
 * parcourt en galerie ne veut pas refaire son choix a chaque endroit ou ses
 * produits s'affichent, et personne ne veut régler la lecture des vidéos deux
 * fois.
 */
const CATALOG_VIEW_KEY = 'bul.catalog-view';
const VIDEO_AUTOPLAY_KEY = 'bul.video-autoplay';
const CURRENT_SHOP_KEY = 'bul.current-shop';

const QUICK_CHECKOUT_KEY = 'bul.quick-checkout';

/**
 * Le dernier moyen de paiement et le dernier mode de réception choisis pour
 * acheter pendant un direct. Ils reviennent cochés à l'achat suivant : c'est ce
 * qui fait tenir la commande en deux appuis, « Acheter » puis « Confirmer ».
 */
export type QuickCheckout = { paymentMethod: PaymentMethod; deliveryMethod: DeliveryMethod };

const PAYMENT_METHODS: ReadonlyArray<PaymentMethod> = [
  'MVOLA',
  'ORANGE_MONEY',
  'AIRTEL_MONEY',
  'CASH',
  'VISA',
  'CHEQUE',
];
const DEFAULT_QUICK_CHECKOUT: QuickCheckout = {
  paymentMethod: 'MVOLA',
  deliveryMethod: 'HAND_DELIVERY',
};

function readQuickCheckout(raw: string | null): QuickCheckout {
  try {
    const parsed = JSON.parse(raw ?? '') as Partial<QuickCheckout>;
    return {
      paymentMethod: PAYMENT_METHODS.includes(parsed.paymentMethod as PaymentMethod)
        ? (parsed.paymentMethod as PaymentMethod)
        : DEFAULT_QUICK_CHECKOUT.paymentMethod,
      deliveryMethod:
        parsed.deliveryMethod === 'COURIER' || parsed.deliveryMethod === 'HAND_DELIVERY'
          ? parsed.deliveryMethod
          : DEFAULT_QUICK_CHECKOUT.deliveryMethod,
    };
  } catch {
    return DEFAULT_QUICK_CHECKOUT;
  }
}
/**
 * L'astuce « Maintenez pour choisir » des réactions a été montrée. Elle ne se
 * montre qu'une fois par appareil : apprise, elle deviendrait du bruit.
 */
const REACTION_HINT_KEY = 'bul.reaction-hint';

/**
 * Quand une vidéo du fil démarre toute seule.
 *
 * Ce réglage n'est pas cosmétique : il décide de la donnée que l'acheteur paie
 * en faisant défiler. Sur un forfait malgache, une dizaine de vidéos parcourues
 * sans y penser se comptent en centaines d'ariary.
 */
export type VideoAutoplay = 'wifi' | 'always' | 'never';

interface PreferencesState {
  /** `true` tant qu'on n'a pas relu le stockage local au démarrage. */
  loading: boolean;
  /** Forme du catalogue du vendeur : liste de travail, ou galerie. */
  catalogView: ViewMode;
  /** Quand les vidéos du fil démarrent seules. */
  videoAutoplay: VideoAutoplay;
  /**
   * La boutique sur laquelle l'espace pro travaille, pour qui en opère
   * plusieurs. `null` : la première. Lue par `useCurrentShop`, qui vérifie que
   * le compte l'opère encore.
   */
  currentShopId: string | null;
  /** Paiement et réception retenus au dernier achat en direct. */
  quickCheckout: QuickCheckout;
  /** L'astuce du tiroir des réactions a déjà été montrée sur cet appareil. */
  reactionHintSeen: boolean;

  hydrate: () => Promise<void>;
  setCatalogView: (mode: ViewMode) => void;
  setVideoAutoplay: (mode: VideoAutoplay) => void;
  setCurrentShop: (shopId: string) => void;
  setQuickCheckout: (choice: QuickCheckout) => void;
  markReactionHintSeen: () => void;
}

const AUTOPLAY_MODES: ReadonlyArray<VideoAutoplay> = ['wifi', 'always', 'never'];

export const usePreferencesStore = create<PreferencesState>((set) => ({
  loading: true,
  // La liste est le mode d'origine : elle porte le code, le prix et le stock,
  // c'est-a-dire de quoi travailler sans rien ouvrir.
  catalogView: 'list',
  // Le Wi-Fi par défaut : le fil reste vivant la ou la donnée est gratuite, et
  // se tient tranquille la ou elle se paie.
  videoAutoplay: 'wifi',
  currentShopId: null,
  // Mobile Money et main propre : les usages les plus courants à Madagascar.
  quickCheckout: DEFAULT_QUICK_CHECKOUT,
  // Vraie tant que le stockage n'est pas relu : une astuce ne s'affiche pas
  // avant qu'on sache si elle a déjà été vue.
  reactionHintSeen: true,

  async hydrate() {
    // Un stockage muet (mode privé du navigateur, trousseau indisponible) n'est
    // pas une panne : on repart sur les modes par défaut.
    const [view, autoplay, shop, checkout, hint] = await Promise.all([
      deviceStorage.read(CATALOG_VIEW_KEY).catch(() => null),
      deviceStorage.read(VIDEO_AUTOPLAY_KEY).catch(() => null),
      deviceStorage.read(CURRENT_SHOP_KEY).catch(() => null),
      deviceStorage.read(QUICK_CHECKOUT_KEY).catch(() => null),
      deviceStorage.read(REACTION_HINT_KEY).catch(() => null),
    ]);
    set({
      loading: false,
      catalogView: view === 'gallery' || view === 'list' ? view : 'list',
      videoAutoplay: AUTOPLAY_MODES.includes(autoplay as VideoAutoplay)
        ? (autoplay as VideoAutoplay)
        : 'wifi',
      currentShopId: shop || null,
      quickCheckout: readQuickCheckout(checkout),
      reactionHintSeen: hint === 'seen',
    });
  },

  setCatalogView(mode) {
    set({ catalogView: mode });
    // L'écran a déja changé : l'écriture ne le fait pas attendre, et son échec
    // ne coute qu'un choix a refaire au prochain lancement.
    void deviceStorage.write(CATALOG_VIEW_KEY, mode).catch(() => undefined);
  },

  setVideoAutoplay(mode) {
    set({ videoAutoplay: mode });
    void deviceStorage.write(VIDEO_AUTOPLAY_KEY, mode).catch(() => undefined);
  },

  setCurrentShop(shopId) {
    set({ currentShopId: shopId });
    void deviceStorage.write(CURRENT_SHOP_KEY, shopId).catch(() => undefined);
  },

  setQuickCheckout(choice) {
    set({ quickCheckout: choice });
    void deviceStorage.write(QUICK_CHECKOUT_KEY, JSON.stringify(choice)).catch(() => undefined);
  },

  markReactionHintSeen() {
    set({ reactionHintSeen: true });
    void deviceStorage.write(REACTION_HINT_KEY, 'seen').catch(() => undefined);
  },
}));

/** Raccourcis de lecture, pour éviter de réécrire le sélecteur partout. */
export const useCatalogView = (): ViewMode => usePreferencesStore((state) => state.catalogView);
export const useSetCatalogView = (): ((mode: ViewMode) => void) =>
  usePreferencesStore((state) => state.setCatalogView);
export const useVideoAutoplay = (): VideoAutoplay =>
  usePreferencesStore((state) => state.videoAutoplay);
export const useSetVideoAutoplay = (): ((mode: VideoAutoplay) => void) =>
  usePreferencesStore((state) => state.setVideoAutoplay);
export const useQuickCheckout = (): QuickCheckout =>
  usePreferencesStore((state) => state.quickCheckout);
export const useSetQuickCheckout = (): ((choice: QuickCheckout) => void) =>
  usePreferencesStore((state) => state.setQuickCheckout);
